import { Box, HStack, StackDivider, Text, VStack } from '@chakra-ui/react';
import { CrowdStrikeMark, MsDefenderMark } from '@gamma/ui/logos';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useContext } from 'react';
import { ROUTES } from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import { useEntitiesContext, useEntityEdrContext } from '../../context';
import {
  CrowdstrikeModalSection,
  MSDefenderModalSection,
  NoDataSection,
  NotConfiguredSection,
  PaloAltoModalSection,
  UserHistorySection,
} from '../EDRDetailsSections';

const { browseIntegrations } = i18n.pages.entityDetails;
const {
  additionalEdrIntegration,
  crowdStrike: crowdStrikeI18n,
  microsoftDefender,
} = i18n.pages.integrations;

export const EDRDetailsTab = (): JSX.Element => {
  const { userPrivileges, isOrgTenant } = useContext(AuthContext);
  const {
    selectedEntity,
    analysisModal: { onClose },
  } = useEntitiesContext();

  // Get EDR data from context
  const { edrData: organizedData, loading, error } = useEntityEdrContext();

  const { tenantInfo } = selectedEntity || {};
  const { tenant_id = '' } = tenantInfo || {};

  const tenantPrivileges = userPrivileges?.[tenant_id || ''];

  return (
    <Box pt={2.5}>
      {loading ? (
        <GraphQLReqStatus loading={loading} error={error} />
      ) : (
        <VStack alignItems="start" divider={<StackDivider />}>
          {tenantPrivileges?.crowdstrike &&
            (organizedData?.crowdstrike ? (
              <CrowdstrikeModalSection />
            ) : organizedData === null ? (
              <NotConfiguredSection
                header={
                  <HStack>
                    <CrowdStrikeMark height={'20px'} width={'20px'} />
                    <Text textStyle="h5">{crowdStrikeI18n.crowdStrike}</Text>
                  </HStack>
                }
                message={additionalEdrIntegration}
                buttonText={browseIntegrations}
                buttonLink={ROUTES.systemSettingsIntegrationsConfig}
                hideButton={isOrgTenant}
                onButtonClick={onClose}
              />
            ) : (
              <NoDataSection
                header={
                  <HStack>
                    <CrowdStrikeMark height={'20px'} width={'20px'} />
                    <Text textStyle="h5">{crowdStrikeI18n.crowdStrike}</Text>
                  </HStack>
                }
              />
            ))}
          {tenantPrivileges?.microsoft_defender &&
            (organizedData?.microsoft_defender ? (
              <MSDefenderModalSection data={organizedData.microsoft_defender} />
            ) : organizedData === null ? (
              <NotConfiguredSection
                header={
                  <HStack>
                    <MsDefenderMark />
                    <Text textStyle="h5">{microsoftDefender}</Text>
                  </HStack>
                }
                message={additionalEdrIntegration}
                buttonText={browseIntegrations}
                buttonLink={ROUTES.systemSettingsIntegrationsConfig}
                hideButton={isOrgTenant}
                onButtonClick={onClose}
              />
            ) : (
              <NoDataSection
                header={
                  <HStack>
                    <MsDefenderMark />
                    <Text textStyle="h5">{microsoftDefender}</Text>
                  </HStack>
                }
              />
            ))}
          {tenantPrivileges?.palo_alto_networks && <PaloAltoModalSection />}
          {(tenantPrivileges?.crowdstrike ||
            tenantPrivileges?.microsoft_entra_id) && (
            <UserHistorySection
              tenantPrivileges={tenantPrivileges}
              organizedData={organizedData}
            />
          )}
        </VStack>
      )}
    </Box>
  );
};
